import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getTenantAccessAction, getTenantSettings, getUserDetails, logout } from '../actions/userActions';
import { BUILD_META } from '../generatedBuildMeta';

const DEFAULT_SESSION_TIMEOUT_MINUTES = 30;
const MIN_SESSION_TIMEOUT_MINUTES = 5;
const MAX_SESSION_TIMEOUT_MINUTES = 240;
const VERSION_CHECK_INTERVAL_MS = 3 * 60 * 1000;
const USER_SYNC_INTERVAL_MS = 5 * 60 * 1000;
const ACTIVITY_BROADCAST_THROTTLE_MS = 15 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
const SESSION_ACTIVITY_STORAGE_KEY = 'dashboardSessionActivityAt';
const SESSION_LOGOUT_STORAGE_KEY = 'dashboardSessionLogoutSignal';
const SESSION_CONFIG_STORAGE_KEY = 'dashboardSessionTimeoutConfig';
const VERSION_SIGNAL_STORAGE_KEY = 'dashboardVersionUpdateSignal';
const AUTH_STORAGE_KEY = 'userInfo';
const PROTECTED_PROFILE_REFRESH_PATTERNS = [
  /^\/tenancy_profile$/,
  /^\/account\/settings$/,
  /^\/account\/tax-offices$/,
  /^\/account\/distribution-zones$/,
  /^\/category\/create$/,
  /^\/demand-notice-categories\/[^/]+\/edit$/,
  /^\/revenuelines\/[^/]+\/edit$/,
  /^\/demand_selection$/,
  /^\/demand_module$/,
  /^\/demand-notices\/[^/]+$/,
  /^\/demand-notices\/specific\/[^/]+$/,
];

const SAFE_AUTO_RESET_PATHS = new Set([
  '/',
  '/tenancy_profile',
  '/field-agent-access',
  '/account/tax-offices',
  '/account/distribution-zones',
  '/dashboard/profile',
  '/dashboard/payments',
  '/category',
  '/audit-logs',
  '/demand-notices/batches',
  '/distributions/dashboard',
  '/distributions/list',
  '/distributions/flagged',
  '/distributions/map',
  '/distributions/reports/records',
  '/super-admin/dashboard',
  '/super-admin/tenants',
  '/super-admin/audit-logs',
  '/super-admin/analytics',
  '/super-admin/owner-reports',
  '/super-admin/platform-users',
  '/super-admin/access',
]);

const resolveConfiguredDefaultSessionTimeoutMinutes = () => {
  const configuredMinutes = Number(process.env.REACT_APP_SESSION_TIMEOUT_MINUTES);

  if (Number.isFinite(configuredMinutes) && configuredMinutes > 0) {
    return configuredMinutes;
  }

  return DEFAULT_SESSION_TIMEOUT_MINUTES;
};

const FALLBACK_SESSION_TIMEOUT_MINUTES = resolveConfiguredDefaultSessionTimeoutMinutes();

const normalizeSessionTimeoutMinutes = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return FALLBACK_SESSION_TIMEOUT_MINUTES;
  }

  return Math.min(MAX_SESSION_TIMEOUT_MINUTES, Math.max(MIN_SESSION_TIMEOUT_MINUTES, Math.round(parsed)));
};

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
    return JSON.parse(payload);
  } catch {
    return null;
  }
};

const getTokenExpiryTimestamp = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return null;
  }

  return payload.exp * 1000;
};

const buildSessionNotice = (reason, timeoutMinutes) => JSON.stringify({
  reason,
  timeoutMinutes,
  occurredAt: Date.now(),
});

const parseJsonPayload = (rawValue) => {
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch {
    return null;
  }
};

const isSafeAutoResetPath = (pathname) => SAFE_AUTO_RESET_PATHS.has(String(pathname || '').trim());
const isProtectedProfileRefreshPath = (pathname) => PROTECTED_PROFILE_REFRESH_PATTERNS.some((pattern) => pattern.test(String(pathname || '').trim()));

const readLatestBuildMeta = async () => {
  const response = await fetch(`/version.json?t=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to check the latest dashboard build version.');
  }

  return await response.json();
};

const DashboardRuntimeGuard = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const { tenant } = useSelector((state) => state.tenantDashboardDetails || {});
  const { access: tenantAccess } = useSelector((state) => state.userTenantAccess || {});
  const tenantId = tenant?.singleTenant?._id || userInfo?.tenantId || '';
  const sessionTimerRef = useRef(null);
  const lastActivityBroadcastRef = useRef(0);
  const logoutInFlightRef = useRef(false);
  const latestRemoteLogoutAtRef = useRef(0);
  const currentBuildVersion = String(BUILD_META.version || 'dev-local').trim();
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(FALLBACK_SESSION_TIMEOUT_MINUTES);
  const [versionUpdate, setVersionUpdate] = useState(null);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const shouldPauseProfileRefresh = isProtectedProfileRefreshPath(location.pathname);

  const sessionTimeoutMs = useMemo(
    () => normalizeSessionTimeoutMinutes(sessionTimeoutMinutes) * 60 * 1000,
    [sessionTimeoutMinutes],
  );

  const syncTenantSessionConfig = useCallback(async () => {
    if (!userInfo?.accessToken || !tenantId) {
      setSessionTimeoutMinutes(FALLBACK_SESSION_TIMEOUT_MINUTES);
      return;
    }

    try {
      const response = await dispatch(getTenantSettings({ freshnessToken: Date.now() }));
      const nextMinutes = normalizeSessionTimeoutMinutes(response?.data?.sessionTimeoutMinutes);
      setSessionTimeoutMinutes(nextMinutes);
    } catch {
      setSessionTimeoutMinutes(FALLBACK_SESSION_TIMEOUT_MINUTES);
    }
  }, [dispatch, tenantId, userInfo?.accessToken]);

  const performLogout = useCallback((reason, options = {}) => {
    if (logoutInFlightRef.current) {
      return;
    }

    logoutInFlightRef.current = true;

    const noticeTimeoutMinutes = normalizeSessionTimeoutMinutes(options.timeoutMinutes ?? sessionTimeoutMinutes);

    if (reason === 'inactive' || reason === 'expired') {
      sessionStorage.setItem('dashboardSessionNotice', buildSessionNotice(reason, noticeTimeoutMinutes));
    }

    if (options.broadcast) {
      localStorage.setItem(SESSION_LOGOUT_STORAGE_KEY, JSON.stringify({
        reason,
        timeoutMinutes: noticeTimeoutMinutes,
        occurredAt: Date.now(),
      }));
    }

    dispatch(logout());
  }, [dispatch, sessionTimeoutMinutes]);

  const announceVersionUpdate = useCallback((latestVersion) => {
    const normalizedLatestVersion = String(latestVersion || '').trim();
    if (!normalizedLatestVersion || normalizedLatestVersion === currentBuildVersion) {
      return;
    }

    setVersionUpdate((current) => {
      if (current?.latestVersion === normalizedLatestVersion) {
        return current;
      }

      return {
        latestVersion: normalizedLatestVersion,
        detectedAt: Date.now(),
      };
    });

    localStorage.setItem(VERSION_SIGNAL_STORAGE_KEY, JSON.stringify({
      latestVersion: normalizedLatestVersion,
      detectedAt: Date.now(),
    }));
  }, [currentBuildVersion]);

  const checkLatestVersion = useCallback(async () => {
    try {
      const latestBuildMeta = await readLatestBuildMeta();
      const latestVersion = String(latestBuildMeta?.version || '').trim();
      if (latestVersion && latestVersion !== currentBuildVersion) {
        announceVersionUpdate(latestVersion);
      }
    } catch {
      // Ignore transient version-check failures.
    }
  }, [announceVersionUpdate, currentBuildVersion]);

  useEffect(() => {
    if (!userInfo?.accessToken) {
      logoutInFlightRef.current = false;
      setSessionTimeoutMinutes(FALLBACK_SESSION_TIMEOUT_MINUTES);
      return undefined;
    }

    if (shouldPauseProfileRefresh) {
      return undefined;
    }

    const syncCurrentUser = () => {
      dispatch(getUserDetails());
      if (tenantId) {
        dispatch(getTenantAccessAction(tenantId)).catch(() => undefined);
      }
      syncTenantSessionConfig();
    };

    const handleWindowFocus = () => {
      syncCurrentUser();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        syncCurrentUser();
      }
    };

    syncCurrentUser();

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const refreshInterval = window.setInterval(syncCurrentUser, USER_SYNC_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(refreshInterval);
    };
  }, [dispatch, shouldPauseProfileRefresh, syncTenantSessionConfig, tenantId, userInfo?.accessToken]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== SESSION_CONFIG_STORAGE_KEY || !event.newValue) {
        return;
      }

      const payload = parseJsonPayload(event.newValue);
      const nextMinutes = normalizeSessionTimeoutMinutes(payload?.sessionTimeoutMinutes);
      if (payload?.tenantId && String(payload.tenantId) !== String(tenantId || '')) {
        return;
      }

      setSessionTimeoutMinutes(nextMinutes);
    };

    const handleLocalUpdate = (event) => {
      const nextMinutes = normalizeSessionTimeoutMinutes(event?.detail?.sessionTimeoutMinutes);
      setSessionTimeoutMinutes(nextMinutes);
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('dashboard-session-timeout-updated', handleLocalUpdate);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('dashboard-session-timeout-updated', handleLocalUpdate);
    };
  }, [tenantId]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!userInfo?.accessToken || !tenantId) {
      return undefined;
    }

    if (String(tenantAccess?.tenantId || '') === String(tenantId)) {
      return undefined;
    }

    dispatch(getTenantAccessAction(tenantId)).catch(() => {
      // Allow screens to fall back gracefully if tenant access refresh fails.
    });
  }, [dispatch, tenantAccess?.tenantId, tenantId, userInfo?.accessToken]);

  useEffect(() => {
    if (!userInfo?.accessToken) {
      if (sessionTimerRef.current) {
        clearTimeout(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
      return undefined;
    }

    const tokenExpiry = getTokenExpiryTimestamp(userInfo.accessToken);

    const clearSessionTimer = () => {
      if (sessionTimerRef.current) {
        clearTimeout(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
    };

    const hasExpiredToken = () => {
      if (!tokenExpiry) {
        return false;
      }

      if (Date.now() >= tokenExpiry) {
        performLogout('expired', { broadcast: true });
        return true;
      }

      return false;
    };

    const scheduleSessionTimeout = () => {
      clearSessionTimer();
      sessionTimerRef.current = setTimeout(() => {
        performLogout('inactive', { broadcast: true });
      }, sessionTimeoutMs);
    };

    const broadcastActivityIfNeeded = () => {
      const now = Date.now();
      if (now - lastActivityBroadcastRef.current < ACTIVITY_BROADCAST_THROTTLE_MS) {
        return;
      }

      lastActivityBroadcastRef.current = now;
      localStorage.setItem(SESSION_ACTIVITY_STORAGE_KEY, String(now));
    };

    const handleActivity = () => {
      if (hasExpiredToken()) {
        return;
      }

      scheduleSessionTimeout();
      broadcastActivityIfNeeded();
    };

    const handleStorage = (event) => {
      if (event.key === SESSION_ACTIVITY_STORAGE_KEY && event.newValue) {
        if (!hasExpiredToken()) {
          scheduleSessionTimeout();
        }
        return;
      }

      if (event.key === SESSION_LOGOUT_STORAGE_KEY && event.newValue) {
        const remoteLogout = parseJsonPayload(event.newValue);
        const occurredAt = Number(remoteLogout?.occurredAt || 0);
        if (!occurredAt || occurredAt <= latestRemoteLogoutAtRef.current) {
          return;
        }

        latestRemoteLogoutAtRef.current = occurredAt;
        performLogout(remoteLogout?.reason || 'signed_out', {
          broadcast: false,
          timeoutMinutes: remoteLogout?.timeoutMinutes,
        });
        return;
      }

      if (event.key === AUTH_STORAGE_KEY && !event.newValue && userInfo?.accessToken) {
        performLogout('signed_out', { broadcast: false });
      }
    };

    if (hasExpiredToken()) {
      return undefined;
    }

    scheduleSessionTimeout();
    broadcastActivityIfNeeded();

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    window.addEventListener('storage', handleStorage);
    const tokenCheckInterval = window.setInterval(hasExpiredToken, 60 * 1000);

    return () => {
      clearSessionTimer();
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      window.removeEventListener('storage', handleStorage);
      window.clearInterval(tokenCheckInterval);
    };
  }, [performLogout, sessionTimeoutMs, userInfo?.accessToken]);

  useEffect(() => {
    checkLatestVersion();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkLatestVersion();
      }
    };

    const handleFocus = () => {
      checkLatestVersion();
    };

    const handleStorage = (event) => {
      if (event.key !== VERSION_SIGNAL_STORAGE_KEY || !event.newValue) {
        return;
      }

      const payload = parseJsonPayload(event.newValue);
      const latestVersion = String(payload?.latestVersion || '').trim();
      if (latestVersion && latestVersion !== currentBuildVersion) {
        setVersionUpdate({
          latestVersion,
          detectedAt: Number(payload?.detectedAt || Date.now()),
        });
      }
    };

    const intervalId = window.setInterval(checkLatestVersion, VERSION_CHECK_INTERVAL_MS);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorage);
    };
  }, [checkLatestVersion, currentBuildVersion]);

  useEffect(() => {
    if (!versionUpdate || !isOnline || !isSafeAutoResetPath(location.pathname)) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      window.location.replace('/__origin-reset.html');
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isOnline, location.pathname, versionUpdate]);

  return null;
};

export default DashboardRuntimeGuard;
